<script setup>
// NOTICE: KAPTION-INTEGRATION
// Inline editor for Kaption utility templates (single {{1}} variable)
// Displays the template with inline variable editing - the editor IS the preview
// Supports file attachments via drag-drop or button click
import { escapeHtml } from 'shared/helpers/HTMLSanitizer';
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import kaptionTemplateAttachmentsAPI from 'dashboard/api/kaptionTemplateAttachments';
import { convertEmojiShortcodes } from 'shared/helpers/emojiShortcodes';
// NOTICE: KAPTION-INTEGRATION - The {{1}} variable of a WhatsApp template must
// stay on a single line (Meta rejects newlines in template parameters), so
// everything pasted, dropped or set programmatically is flattened first.
import {
  extractPlainText,
  flattenToSingleLine,
  hasMultiLineMarkup,
  needsFlattening,
  stripLineBreaks,
} from 'dashboard/helper/kaptionSingleLineText';

const props = defineProps({
  /**
   * The template object containing body with {{1}} variable
   */
  template: {
    type: Object,
    required: true,
    validator: t => t?.body && typeof t.body === 'string',
  },
  /**
   * The variable value (v-model)
   */
  modelValue: {
    type: String,
    default: '',
  },
  /**
   * Placeholder text for the variable input
   */
  placeholder: {
    type: String,
    default: '',
  },
  /**
   * Whether to use the template example as placeholder
   */
  showExamplePlaceholder: {
    type: Boolean,
    default: false,
  },
  /**
   * Disable editing
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Enable attachment upload feature
   */
  enableAttachments: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'valid', 'attachmentUploaded']);

// whatsapp-utility-template-input.INPUT_INTEGRITY.3 — Enter is blocked by the
// contenteditable's keydown modifier below.
const { t } = useI18n();

// Local state
const localValue = ref(props.modelValue);
const inputRef = ref(null);
const fileInputRef = ref(null);
const isInternalUpdate = ref(false);

// Attachment state
const isUploading = ref(false);
const isDragging = ref(false);
const uploadedAttachment = ref(null);

// Maximum file size (15MB)
const MAX_FILE_SIZE = 15 * 1024 * 1024;

// Allowed file types
const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'video/mp4',
  'video/quicktime',
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
];

// NOTICE: KAPTION-INTEGRATION - Push a value into the editor, guaranteeing it is
// a single line. Emits back up when flattening changed the incoming value so the
// parent never holds a multi-line variable we would refuse to render.
const applyExternalValue = rawValue => {
  const value = flattenToSingleLine(rawValue);
  localValue.value = value;
  if (inputRef.value && inputRef.value.textContent !== value) {
    inputRef.value.textContent = value;
  }
  if (value !== (rawValue || '')) {
    isInternalUpdate.value = true;
    emit('update:modelValue', value);
  }
};

// Set initial value on mount
onMounted(() => {
  if (inputRef.value && props.modelValue) {
    applyExternalValue(props.modelValue);
  }
});

// Sync with prop (only when changed externally)
watch(
  () => props.modelValue,
  newVal => {
    if (!isInternalUpdate.value && inputRef.value) {
      applyExternalValue(newVal);
    }
    isInternalUpdate.value = false;
  }
);

// Computed placeholder
const computedPlaceholder = computed(() => {
  if (props.placeholder) return props.placeholder;
  if (props.showExamplePlaceholder && props.template?.examples?.length) {
    return props.template.examples[0];
  }
  // NOTICE: KAPTION-INTEGRATION - Key is at root level, not inside CONVERSATION
  // (conversation.json is merged into the root of each locale bundle).
  // Deliberately no English default message: `en` now carries this string like
  // every other supported locale, so the placeholder always follows the agent's
  // UI language instead of silently falling back to a hardcoded literal.
  // whatsapp-utility-template-input.LOCALIZATION.1
  return t('KAPTION_UTILITY_TEMPLATES.VARIABLE_PLACEHOLDER');
});

// Parse template into parts (before {{1}}, the variable, after {{1}})
const templateParts = computed(() => {
  const body = props.template?.body || '';
  const match = body.match(/^([\s\S]*?)\{\{1\}\}([\s\S]*)$/);

  if (!match) {
    // No {{1}} found, return entire body as prefix
    return { prefix: body, suffix: '' };
  }

  return {
    prefix: match[1],
    suffix: match[2],
  };
});

// Check if variable is filled
const isFilled = computed(() => (localValue.value || '').trim().length > 0);

// Emit validity
watch(
  isFilled,
  valid => {
    emit('valid', valid);
  },
  { immediate: true }
);

// NOTICE: KAPTION-INTEGRATION - Put the caret back at the end after we rewrite
// the editor's contents, otherwise the user is thrown to the start of the field.
const setCaretToEnd = element => {
  if (!element || typeof window === 'undefined') return;
  const selection = window.getSelection?.();
  if (!selection || typeof document.createRange !== 'function') return;
  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

// Handle input - convert emoji shortcodes and sync the value
const handleInput = event => {
  const element = event.target;
  // NOTICE: KAPTION-INTEGRATION - A native contenteditable paste leaves <div>/<br>
  // children behind. `textContent` would glue the lines together with no
  // separator ("Line oneLine two"), so read the DOM line-aware instead and
  // rebuild it as plain text whenever markup snuck in.
  const domText = element.textContent || '';
  const brokeSingleLine = hasMultiLineMarkup(element);

  const displayText = convertEmojiShortcodes(
    brokeSingleLine ? flattenToSingleLine(extractPlainText(element)) : domText
  );

  // Only touch the DOM when it is genuinely wrong - rewriting it on every
  // keystroke would drag the caret to the end while the user types mid-word.
  if (displayText !== domText) {
    element.textContent = displayText;
    setCaretToEnd(element);
  }

  // The emitted value never carries line breaks, even if the DOM somehow does.
  const value = stripLineBreaks(displayText);
  isInternalUpdate.value = true;
  localValue.value = value;
  emit('update:modelValue', value);
};

// NOTICE: KAPTION-INTEGRATION - Insert plain text at the caret, keeping native
// undo where the browser supports it.
const insertTextAtCaret = text => {
  const element = inputRef.value;
  if (!element || !text) return;

  element.focus();

  let inserted = false;
  try {
    inserted = document.execCommand?.('insertText', false, text) || false;
  } catch (error) {
    inserted = false;
  }

  // execCommand fires a real `input` event, so handleInput already synced the
  // value and the caret stayed where the user put it. Nothing else to do.
  if (inserted) return;

  const selection = window.getSelection?.();
  const hasCaretInside =
    selection?.rangeCount &&
    element.contains(selection.getRangeAt(0).commonAncestorContainer);

  if (hasCaretInside) {
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    element.textContent = `${element.textContent || ''}${text}`;
    setCaretToEnd(element);
  }

  // The manual paths do not fire `input`, so sync the model explicitly.
  const value = stripLineBreaks(element.textContent || '');
  isInternalUpdate.value = true;
  localValue.value = value;
  emit('update:modelValue', value);
};

// NOTICE: KAPTION-INTEGRATION - Take over paste so multi-line content lands on a
// single line instead of being silently glued together by textContent.
// whatsapp-utility-template-input.INPUT_INTEGRITY.1
// whatsapp-utility-template-input.INPUT_INTEGRITY.4
const handleFlattenedTextInsert = rawText => {
  const wasMultiLine = needsFlattening(rawText);
  const text = flattenToSingleLine(rawText);
  if (!text) return;

  insertTextAtCaret(text);

  if (wasMultiLine) {
    useAlert(
      t(
        'KAPTION_UTILITY_TEMPLATES.MULTILINE_FLATTENED',
        'Pasted text was joined into a single line - template variables cannot contain line breaks.'
      )
    );
  }
};

const handlePaste = event => {
  if (props.disabled) return;
  const clipboard = event.clipboardData || window.clipboardData;
  if (!clipboard) return;
  event.preventDefault();
  handleFlattenedTextInsert(clipboard.getData('text/plain') || '');
};

// Focus the input
const focusInput = () => {
  inputRef.value?.focus();
};

// Format text for display (handle newlines and markdown bold)
// CW-H7: template text is escaped before the *bold* / newline markup is added,
// so markup inside a template body renders as text instead of running in v-html.
const formatText = text => {
  return escapeHtml(text || '')
    .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>');
};

// ========== Attachment Handling ==========

// Validate file before upload
const validateFile = file => {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: t(
        'KAPTION_UTILITY_TEMPLATES.ATTACHMENT_TOO_LARGE',
        'File size exceeds 15MB limit'
      ),
    };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: t(
        'KAPTION_UTILITY_TEMPLATES.ATTACHMENT_TYPE_NOT_ALLOWED',
        'File type not allowed'
      ),
    };
  }

  return { valid: true };
};

// Upload file to shortener service
const uploadFile = async file => {
  const validation = validateFile(file);
  if (!validation.valid) {
    useAlert(validation.error);
    return;
  }

  isUploading.value = true;

  try {
    const response = await kaptionTemplateAttachmentsAPI.upload(file);
    const { url, hash, filename } = response.data;

    uploadedAttachment.value = {
      url,
      hash,
      filename,
      contentType: file.type,
    };

    // Append the short URL to the current value
    const currentText = flattenToSingleLine(localValue.value);
    const newValue = currentText ? `${currentText} ${url}` : url;

    isInternalUpdate.value = true;
    localValue.value = newValue;
    if (inputRef.value) {
      inputRef.value.textContent = newValue;
    }
    emit('update:modelValue', newValue);
    emit('attachmentUploaded', { url, hash, filename });

    useAlert(
      t(
        'KAPTION_UTILITY_TEMPLATES.ATTACHMENT_UPLOADED',
        'File uploaded successfully'
      )
    );
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      t('KAPTION_UTILITY_TEMPLATES.ATTACHMENT_UPLOAD_FAILED', 'Upload failed');
    useAlert(errorMessage);
  } finally {
    isUploading.value = false;
  }
};

// Handle file input change
const handleFileSelect = event => {
  const file = event.target.files?.[0];
  if (file) {
    uploadFile(file);
  }
  // Reset file input to allow selecting the same file again
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

// Trigger file picker
const triggerFilePicker = () => {
  fileInputRef.value?.click();
};

// Handle drag events
const handleDragEnter = event => {
  event.preventDefault();
  event.stopPropagation();
  if (props.enableAttachments && !props.disabled) {
    isDragging.value = true;
  }
};

const handleDragLeave = event => {
  event.preventDefault();
  event.stopPropagation();
  isDragging.value = false;
};

const handleDragOver = event => {
  event.preventDefault();
  event.stopPropagation();
};

const handleDrop = event => {
  event.preventDefault();
  event.stopPropagation();
  isDragging.value = false;

  if (props.disabled) return;

  const file = event.dataTransfer?.files?.[0];
  if (file) {
    if (props.enableAttachments) uploadFile(file);
    return;
  }

  // NOTICE: KAPTION-INTEGRATION - Dropped text used to be swallowed entirely,
  // because preventDefault() ran and only files were handled. Insert it instead,
  // flattened onto a single line like a paste.
  // whatsapp-utility-template-input.INPUT_INTEGRITY.2
  const droppedText = event.dataTransfer?.getData?.('text/plain') || '';
  if (droppedText) {
    handleFlattenedTextInsert(droppedText);
  }
};

// Clear uploaded attachment
const clearAttachment = () => {
  uploadedAttachment.value = null;
};

// NOTICE: KAPTION-INTEGRATION - Expose methods to parent for bottom panel integration
defineExpose({
  triggerFilePicker,
  isUploading,
  uploadedAttachment,
  clearAttachment,
});
</script>

<template>
  <div class="kaption-inline-utility-editor">
    <!-- Editor Area - Inline Template Style -->
    <div
      class="template-editor"
      :class="{
        'is-disabled': disabled,
        'is-dragging': isDragging,
      }"
      @click="focusInput"
      @dragenter="handleDragEnter"
      @dragleave="handleDragLeave"
      @dragover="handleDragOver"
      @drop="handleDrop"
    >
      <!-- Prefix text --><span
        v-if="templateParts.prefix"
        class="template-text"
        v-html="formatText(templateParts.prefix)"
      /><span
        ref="inputRef"
        class="inline-variable"
        :class="{
          'is-empty': !isFilled,
          'is-filled': isFilled,
        }"
        :data-placeholder="computedPlaceholder"
        :contenteditable="!disabled"
        @input="handleInput"
        @paste="handlePaste"
        @keydown.enter.prevent
      /><span
        v-if="templateParts.suffix"
        class="template-text"
        v-html="formatText(templateParts.suffix)"
      />

      <!-- Drag overlay -->
      <div v-if="isDragging" class="drag-overlay">
        <span class="drag-overlay-text">
          {{ t('KAPTION_UTILITY_TEMPLATES.DROP_FILE_HERE', 'Drop file here') }}
        </span>
      </div>
    </div>

    <!-- NOTICE: KAPTION-INTEGRATION - Hidden file input (button moved to bottom panel) -->
    <input
      v-if="enableAttachments"
      ref="fileInputRef"
      type="file"
      class="hidden"
      :accept="ALLOWED_TYPES.join(',')"
      @change="handleFileSelect"
    />
  </div>
</template>

<style scoped lang="scss">
.kaption-inline-utility-editor {
  @apply flex flex-col;
}

/* Editor Container */
.template-editor {
  @apply p-4 rounded-xl cursor-text relative;
  @apply bg-n-solid-1 border border-n-weak;
  @apply text-base leading-relaxed text-n-slate-12;
  font-size: 1.0625rem;
  line-height: 1.75;
  transition: border-color 0.15s ease;

  &.is-disabled {
    @apply opacity-60 pointer-events-none;
  }

  &.is-dragging {
    @apply border-n-brand border-2;
    background: rgb(var(--brand-3) / 0.1);
  }
}

/* Static template text */
.template-text {
  @apply text-n-slate-12;

  :deep(strong) {
    @apply font-semibold;
  }
}

/* Inline Variable Input */
.inline-variable {
  @apply inline outline-none border-none bg-transparent;
  font: inherit;
  color: inherit;
  line-height: inherit;
  min-width: 3ch;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-decoration-color: rgb(var(--slate-6));
  text-underline-offset: 4px;
  text-decoration-thickness: 2px;
  cursor: text;
  transition: all 0.15s ease;

  &.is-empty {
    text-decoration-color: rgb(var(--ruby-6));
    @apply text-n-slate-10;

    &::before {
      content: attr(data-placeholder);
      @apply text-n-ruby-9 italic;
    }

    &:not(:empty)::before {
      content: '';
    }
  }

  /* NOTICE: KAPTION-INTEGRATION - Use green tone for filled state */
  &.is-filled {
    text-decoration-style: solid;
    text-decoration-color: rgb(var(--green-7));
    @apply text-n-green-11 font-medium;
  }

  &:hover {
    background: rgb(var(--green-3) / 0.4);
    text-decoration-color: rgb(var(--green-9));
  }

  &.is-empty:hover {
    background: rgb(var(--ruby-3) / 0.3);
    text-decoration-color: rgb(var(--ruby-9));
  }

  &:focus {
    background: rgb(var(--green-3) / 0.5);
    text-decoration-color: rgb(var(--green-9));
    text-decoration-style: solid;
  }
}

/* Drag overlay */
.drag-overlay {
  @apply absolute inset-0 flex items-center justify-center;
  @apply bg-n-brand/10 rounded-xl;
  pointer-events: none;
}

.drag-overlay-text {
  @apply text-n-brand font-medium text-sm;
  @apply bg-n-solid-1 px-3 py-1.5 rounded-lg shadow-sm;
}

/* NOTICE: KAPTION-INTEGRATION - Attachment button moved to bottom panel */
</style>
